import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config/constants";
import "../css/Header.css";

const categories = {
  girl: {
    name: "Girl",
    subcategories: ["Cotton Wear", "Party Frocks", "Premium Ethinc", "Western"],
  },
  women: {
    name: "Women",
    subcategories: ["Luxe Cordset", "Premium Pakistani", "Jeans"],
  },
};

const IndianFlag = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 16"
    width="18"
    height="12"
    style={{
      marginLeft: "8px",
      borderRadius: "2px",
      display: "inline-block",
      verticalAlign: "middle",
      boxShadow: "0 0 1px rgba(0,0,0,0.5)",
      flexShrink: 0,
    }}
    aria-label="Flag of India"
  >
    <rect width="24" height="16" fill="#138808" />
    <rect width="24" height="10.667" fill="#FFFFFF" />
    <rect width="24" height="5.333" fill="#FF9933" />
    <circle cx="12" cy="8" r="2.2" fill="none" stroke="#000080" strokeWidth="0.5" />
    <circle cx="12" cy="8" r="0.5" fill="#000080" />
    <path
      d="M12 5.8 v4.4 M9.8 8 h4.4 M10.4 6.4 l3.2 3.2 M10.4 9.6 l3.2 -3.2 M11.2 5.9 l1.6 4.2 M11.2 10.1 l1.6 -4.2 M10.1 7.2 l3.8 1.6 M10.1 8.8 l3.8 -1.6"
      stroke="#000080"
      strokeWidth="0.25"
    />
  </svg>
);

function AminiiNavbar({ isLoggedIn, userFirstName, cartItemCount, onLogout }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [logoutSuccess, setLogoutSuccess] = useState(false);

  const searchContainerRef = useRef(null);
  const profileContainerRef = useRef(null);

  const isMainImageCat = (cat) =>
    cat?.categoryName?.toLowerCase().replace(/[-_\s]+/g, "") === "mainimage" ||
    cat?.subCategory?.categoryName?.toLowerCase().replace(/[-_\s]+/g, "") === "mainimage";

  const isMainImageProd = (p) =>
    (p.productCategory && p.productCategory.some(isMainImageCat)) ||
    (p.productName && p.productName.toLowerCase().replace(/[-_\s]+/g, "") === "mainimage");

  useEffect(() => {
    const fetchSuggestions = async () => {
      const query = searchQuery.trim();
      if (query.length < 2) {
        setSuggestions([]);
        return;
      }
      try {
        const response = await axios.get(
          `${API_BASE_URL}/products/search?q=${encodeURIComponent(query)}&limit=10`,
          { withCredentials: true }
        );
        const rawResults = response.data?.data ?? response.data ?? [];
        const filtered = (Array.isArray(rawResults) ? rawResults : []).filter(
          (product) => !isMainImageProd(product)
        );
        setSuggestions(filtered.slice(0, 5));
      } catch {
        setSuggestions([]);
      }
    };
    const debounce = setTimeout(fetchSuggestions, 250);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        isSearchOpen &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target)
      ) {
        setIsSearchOpen(false);
        setSearchQuery("");
        setSuggestions([]);
      }
      if (
        isProfileDropdownOpen &&
        profileContainerRef.current &&
        !profileContainerRef.current.contains(e.target)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [isSearchOpen, isProfileDropdownOpen]);

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  const toggleSearch = () => {
    setIsSearchOpen((prev) => {
      if (prev) {
        setSearchQuery("");
        setSuggestions([]);
      }
      return !prev;
    });
  };

  const toggleProfileDropdown = () =>
    setIsProfileDropdownOpen((prev) => !prev);

  const handleLogin = () => {
    navigate("/login", { state: { from: pathname } });
    setIsProfileDropdownOpen(false);
    setIsMenuOpen(false);
  };

  const handleRegister = () => {
    navigate("/register");
    setIsProfileDropdownOpen(false);
    setIsMenuOpen(false);
  };

  const handleCategoryClick = (cat) =>
    setOpenAccordion(openAccordion === cat ? null : cat);

  const handleSubcategoryClick = (category, subcategory) => {
    setIsMenuOpen(false);
    navigate(
      `/productList/${category}/${subcategory.toLowerCase().replace(/\s+/g, "-")}`
    );
  };

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const handleSearchSubmit = async (e) => {
    e?.preventDefault?.();
    if (!searchQuery.trim()) return;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/products/search?q=${encodeURIComponent(searchQuery.trim())}`,
        { withCredentials: true }
      );
      const rawResults = response.data?.data ?? response.data ?? [];
      const results = (Array.isArray(rawResults) ? rawResults : []).filter(
        (product) => !isMainImageProd(product)
      );
      navigate("/productList", {
        state: {
          searchResults: results,
          searchQuery: searchQuery.trim(),
        },
      });
    } catch {
      navigate("/productList", {
        state: { searchResults: [], searchQuery: searchQuery.trim() },
      });
    }
    setIsSearchOpen(false);
    setSearchQuery("");
    setSuggestions([]);
  };

  const handleSuggestionClick = (productId) => {
    navigate(`/productDetails/${productId}`);
    setIsSearchOpen(false);
    setSearchQuery("");
    setSuggestions([]);
  };

  const getFirstImage = (product) =>
    product?.productImages?.[0] ||
    product?.ProductImages?.[0] ||
    "https://placehold.co/50x50?text=No+Image";

  const handleLogoutConfirm = () => {
    axios
      .post(
        `${API_BASE_URL}/logout`,
        {},
        { withCredentials: true }
      )
      .catch(() => {});
    sessionStorage.clear();
    document.cookie = "jwt=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
    document.cookie =
      "refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
    if (onLogout) onLogout();
    setIsMenuOpen(false);
    setIsProfileDropdownOpen(false);
    setLogoutSuccess(true);
    setTimeout(() => {
      setShowLogoutConfirm(false);
      setLogoutSuccess(false);
    }, 1600);
  };

  const baseMessages = [
    "COD AVAILABLE IN KARNATAKA",
    "Free Shipping on Prepaid Orders",
    (
      <span key="all-india" style={{ display: "inline-flex", alignItems: "center" }}>
        All India Delivery Available <IndianFlag />
      </span>
    ),
    "New Arrivals Every Week",
  ];
  // Repeat 8 times (24 items total: 12 in first half, 12 in second half) to seamlessly cover desktop screens
  const announcementMessages = [
    ...baseMessages, ...baseMessages, ...baseMessages, ...baseMessages,
    ...baseMessages, ...baseMessages, ...baseMessages, ...baseMessages,
  ];

  return (
    <header className="amin-header-wrapper">
      <div className="amin-announcement-bar" role="marquee" aria-label="Announcements">
        <div className="amin-ticker-track">
          {announcementMessages.map((msg, i) => (
            <span key={i} className="amin-ticker-item">
              {msg}
              <span className="amin-ticker-sep" aria-hidden="true">✦</span>
            </span>
          ))}
        </div>
      </div>

      <div className="amin-navbar">
        <button
          className={`amin-menu-button ${isMenuOpen ? "open" : ""}`}
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMenuOpen}
        >
          <span className="amin-menu-button-line"></span>
          <span className="amin-menu-button-line"></span>
          <span className="amin-menu-button-line"></span>
        </button>

        <div className="amin-brand-name" onClick={() => navigate("/")}>
          <p className="amin-brand-n">A M I N I I</p>
          <p className="amin-brand-s">C L O T H I N G</p>
        </div>

        <div className="amin-nav-items">
          <button
            onClick={() => navigate("/productList/basics")}
            className="amin-desktop-nav-btn"
          >
            Basics
          </button>
          <button
            onClick={() => navigate("/productList/best-seller")}
            className="amin-desktop-nav-btn"
          >
            Best Seller
          </button>
          <button
            onClick={() => navigate("/productList/aminii-luxe")}
            className="amin-desktop-nav-btn"
          >
            AMINII Luxe
          </button>
          <button
            onClick={() => navigate("/orders")}
            className="amin-desktop-nav-btn"
          >
            My Orders
          </button>
        </div>

        <div className="amin-search-container" ref={searchContainerRef}>
          <button className="amin-search-button" onClick={toggleSearch}>
            <img
              className="search-image"
              src="https://cdn-icons-png.flaticon.com/128/751/751463.png"
              alt="Search"
            />
          </button>
          {isSearchOpen && (
            <div className="amin-search-dropdown">
              <form onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  placeholder="Search for co-ord sets, kurtas, dresses..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  autoFocus
                />
                <button type="submit">Search</button>
              </form>
              {searchQuery.trim().length >= 2 && suggestions.length > 0 ? (
                <>
                <ul className="amin-search-suggestions">
                  {suggestions.map((product) => (
                    <li
                      key={product.productId}
                      className="amin-suggestion-item"
                      onClick={() =>
                        handleSuggestionClick(product.productId)
                      }
                    >
                      <img
                        className="amin-suggestion-image"
                        src={getFirstImage(product)}
                        alt={product.productName || "Product"}
                        onError={(e) => {
                          e.target.src =
                            "https://placehold.co/50x50?text=No+Image";
                          e.target.classList.add("amin-image-error");
                          e.target.onerror = null;
                        }}
                      />
                      <span>{product.productName || "Unnamed Product"}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="amin-search-view-all"
                  onClick={handleSearchSubmit}
                >
                  View all results for &ldquo;{searchQuery.trim()}&rdquo;
                </button>
                </>
              ) : searchQuery.trim().length >= 2 ? (
                <div className="amin-no-results">
                  <p className="amin-no-results-title">
                    No results for &ldquo;
                    <strong>{searchQuery}</strong>&rdquo;
                  </p>
                  <p className="amin-no-results-hint">
                    Browse our full collection instead
                  </p>
                  <button
                    className="amin-no-results-btn"
                    type="button"
                    onClick={() => {
                      navigate("/productList");
                      setIsSearchOpen(false);
                      setSearchQuery("");
                    }}
                  >
                    View All Products
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="amin-profile-container" ref={profileContainerRef}>
          <button
            className="amin-profile-button"
            onClick={toggleProfileDropdown}
          >
            <img
              className="profile-image"
              src="https://cdn-icons-png.flaticon.com/128/1077/1077114.png"
              alt="Profile"
            />
          </button>
          {isProfileDropdownOpen && (
            <div className="amin-profile-dropdown">
              {isLoggedIn ? (
                <>
                  <div className="amin-user-info">
                    <p>Hello, {userFirstName}</p>
                  </div>
                  <button
                    className="amin-logout-btn"
                    onClick={() => setShowLogoutConfirm(true)}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <div className="amin-login-options">
                  <button onClick={handleLogin}>Login</button>
                  <button onClick={handleRegister}>Register</button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="amin-cart-container">
          <button
            className="amin-cart-button"
            onClick={() => window.dispatchEvent(new CustomEvent("aminii-open-cart-drawer"))}
          >
            <img
              className="cart-image"
              src="https://cdn-icons-png.flaticon.com/128/9219/9219671.png"
              alt="Cart"
            />
            {cartItemCount > 0 && (
              <span className="cart-item-count">{cartItemCount}</span>
            )}
          </button>
        </div>
      </div>

      <div className={`amin-sidebar ${isMenuOpen ? "open" : ""}`}>
        <div className="amin-sidebar-nav">
          <button
            className="mobile-subcategory"
            style={{
              fontSize: "15px",
              fontWeight: "600",
              padding: "14px 0",
              borderBottom: "1px solid rgba(0,0,0,0.06)",
              width: "100%",
              textAlign: "left",
              background: "none",
              border: "none",
              color: "#1a1a1a",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
            }}
            onClick={() => {
              setIsMenuOpen(false);
              navigate("/productList");
            }}
          >
            <span>SHOP ALL</span>
          </button>
          {[
            { label: "BASICS", slug: "basics" },
            { label: "BEST SELLER", slug: "best-seller" },
            { label: "AMINII LUXE", slug: "aminii-luxe", badge: "PREMIUM" },
            { label: "PLUS SIZES", slug: "aminii-plus", badge: "4XL - 6XL" },
          ].map((item) => (
            <button
              key={item.slug}
              className="mobile-subcategory"
              style={{
                fontSize: "15px",
                fontWeight: "600",
                padding: "14px 0",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                width: "100%",
                textAlign: "left",
                background: "none",
                border: "none",
                color: "#1a1a1a",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
              }}
              onClick={() => {
                setIsMenuOpen(false);
                navigate(`/productList/${item.slug}`);
              }}
            >
              <span>{item.label}</span>
              {item.badge && (
                <span className="mobile-nav-badge">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
          <button
            onClick={() => {
              navigate("/orders");
              setIsMenuOpen(false);
            }}
            className="My-orders"
          >
            My Orders
          </button>
        </div>
        <div className="amin-sidebar-footer">
          {isLoggedIn ? (
            <>
              <p className="mobile-greeting">Hello, {userFirstName}</p>
              <button
                className="mobile-logout-button"
                onClick={() => setShowLogoutConfirm(true)}
              >
                Logout
              </button>
            </>
          ) : (
            <button className="mobile-login-button" onClick={handleLogin}>
              Login / Register
            </button>
          )}
        </div>
      </div>

      <button
        className={`amin-overlay ${isMenuOpen ? "open" : ""}`}
        onClick={toggleMenu}
      />

      {showLogoutConfirm && (
        <div
          className="amin-confirm-overlay"
          onClick={() => !logoutSuccess && setShowLogoutConfirm(false)}
        >
          <div
            className="amin-confirm-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            {logoutSuccess ? (
              <div className="amin-logout-success">
                <svg
                  className="amin-logout-check"
                  viewBox="0 0 52 52"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="26"
                    cy="26"
                    r="25"
                    stroke="#d4af37"
                    strokeWidth="2"
                  />
                  <path
                    d="M14 26l9 9 15-18"
                    stroke="#d4af37"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <p className="amin-logout-success-text">
                  Logged out successfully
                </p>
              </div>
            ) : (
              <>
                <p className="amin-confirm-title">Logout</p>
                <p className="amin-confirm-message">
                  Are you sure you want to logout?
                </p>
                <div className="amin-confirm-buttons">
                  <button
                    className="amin-confirm-yes"
                    onClick={handleLogoutConfirm}
                  >
                    Yes, Logout
                  </button>
                  <button
                    className="amin-confirm-cancel"
                    onClick={() => setShowLogoutConfirm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default AminiiNavbar;
